import { useEffect, useMemo, useRef, useState } from "react";
import { LockKeyhole } from "lucide-react";
import { formatCountdown } from "../countdown";
import type { DayContent } from "../types";

interface Props {
  days: DayContent[];
  selectedId: string;
  onSelect: (day: DayContent) => void;
}

export function DayNavigation({ days, selectedId, onSelect }: Props) {
  const [now, setNow] = useState(Date.now());
  const navigationRef = useRef<HTMLElement>(null);
  const buttonRefs = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const nextDay = useMemo(
    () => days.find((day) => new Date(day.unlockAt).getTime() > now) ?? null,
    [days, now],
  );

  const selectedDay = days.find((day) => day.id === selectedId) ?? days[0];
  const currentDay = useMemo(
    () => [...days].reverse().find((day) => !day.locked && new Date(day.unlockAt).getTime() <= now) ?? null,
    [days, now],
  );
  const progress = selectedDay ? (selectedDay.number / days.length) * 100 : 0;

  useEffect(() => {
    const selectedButton = buttonRefs.current.get(selectedId);
    if (!selectedButton || !navigationRef.current) return;

    const frame = window.requestAnimationFrame(() => {
      const navigation = navigationRef.current;
      if (!navigation) return;
      const navigationRect = navigation.getBoundingClientRect();
      const buttonRect = selectedButton.getBoundingClientRect();
      const horizontalOffset = (buttonRect.left + buttonRect.width / 2)
        - (navigationRect.left + navigationRect.width / 2);
      navigation.scrollBy({
        left: horizontalOffset,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedId]);

  return (
    <div className="day-navigation-block">
      <div className="day-progress-copy">
        <div>
          <span>המסע שלנו</span>
          <strong>יום {selectedDay?.number ?? 1} מתוך {days.length}</strong>
        </div>
        <span>{selectedDay?.number === 10 ? "יום החתונה" : selectedDay?.dateLabel}</span>
      </div>

      <div
        className="day-progress-track"
        role="progressbar"
        aria-label="התקדמות במסע עשרת הימים"
        aria-valuemin={1}
        aria-valuemax={days.length}
        aria-valuenow={selectedDay?.number ?? 1}
      >
        <span style={{ width: `${progress}%` }} />
      </div>

      <nav ref={navigationRef} className="day-navigation" aria-label="בחירת יום">
        {days.map((day) => {
          const isSelected = day.id === selectedId;
          const isCurrent = day.id === currentDay?.id;
          const stateLabel = day.locked ? "נעול" : isCurrent ? "היום" : isSelected ? "נבחר" : "פתוח";
          const classes = [
            "day-navigation-item",
            day.locked ? "is-locked" : "is-open",
            isCurrent ? "is-current" : "",
            isSelected ? "is-selected" : "",
          ].filter(Boolean).join(" ");

          return (
            <button
              key={day.id}
              ref={(node) => {
                if (node) buttonRefs.current.set(day.id, node);
                else buttonRefs.current.delete(day.id);
              }}
              className={classes}
              disabled={day.locked}
              onClick={() => onSelect(day)}
              aria-current={isCurrent ? "date" : isSelected ? "step" : undefined}
              aria-label={`יום ${day.number}, ${day.dateLabel}, ${stateLabel}`}
            >
              <span className="day-number">
                {day.locked ? <LockKeyhole aria-hidden="true" /> : day.number}
              </span>
              <small>{day.number === 10 ? "החתונה" : day.date}</small>
              <span className="day-state">{stateLabel}</span>
            </button>
          );
        })}
      </nav>

      <div className="next-day-countdown">
        {nextDay ? (
          <>
            <span>הפרק הבא · יום {nextDay.number} · {nextDay.date}</span>
            <strong aria-label={`נפתח בעוד ${formatCountdown(new Date(nextDay.unlockAt).getTime() - now, true)}`}>
              נפתח בעוד {formatCountdown(new Date(nextDay.unlockAt).getTime() - now)}
            </strong>
            <small>לפי לוח הזמנים · 06:00</small>
          </>
        ) : (
          <strong>כל עשרת הפרקים כבר פתוחים</strong>
        )}
      </div>
    </div>
  );
}
